import React, { useEffect, useState, useContext } from "react"
import { db, auth } from "../../../utils/firebase"
import { ModalContext } from "../../../utils/providers/modal"
import moment from "moment"

/**
 * UI components
 */
import Window from "../../structure/window/window"
import WindowCard from "../../ui/window-card/window-card"
import Button from "../../ui/button/button"
import Input from "../../ui/inputs/input"
import Textarea from "../../ui/inputs/textarea"

// User ID that is allowed to archive enquiries from stats
const ARCHIVE_FROM_STATS_USER_ID = "CGOHCdUwFAOe13OSTY2D9qdO2LX2"

/**
 * Functional component to return a window displaying archived enquiry details
 */
function ViewArchivedEnquiry(props) {
	const [client, setClient] = useState({})
	const [agent, setAgent] = useState({})
	const [archiving, setArchiving] = useState(false)
	const [isArchivedFromStats, setIsArchivedFromStats] = useState(false)

	/**
	 * Pull the show modal function from the modal context
	 */
	const { showModal } = useContext(ModalContext)

	/**
	 * Deconstruct the props
	 */
	const { enquiryData, enquiryID, close, onReopen } = props

	/**
	 * Build a string to represent the date from timestamp
	 */
	const dateFromString = moment(enquiryData?.date_from?.seconds, "X").format("MMM D[,] YYYY")
	const followUpString = enquiryData?.follow_up ? moment(enquiryData.follow_up.seconds, "X").format("MMM D[,] YYYY") : "No follow up set"
	const createdString = moment(enquiryData?.created?.seconds, "X").format("MMM D[,] YYYY [at] HH:mm")

	/**
	 * On component load
	 */
	useEffect(() => {
		// Get the client data if available
		if (enquiryData?.client) {
			db.doc(`clients/${enquiryData.client}`)
				.get()
				.then((clientDoc) => {
					if (clientDoc.exists) {
						setClient(clientDoc.data())
					}
				})
				.catch(console.error)
		}

		// Get the agent data if available
		if (enquiryData?.agent) {
			db.doc(`users/${enquiryData.agent}`)
				.get()
				.then((agentDoc) => {
					if (agentDoc.exists) {
						setAgent(agentDoc.data())
					}
				})
				.catch(console.error)
		}

		// Check if this enquiry is already archived from stats
		setIsArchivedFromStats(enquiryData?.archived_from_stats === true)
	}, [enquiryData])

	/**
	 * Check before archiving from stats
	 */
	const checkToArchiveFromStats = () => {
		showModal({
			type: "ALERT",
			title: "Archive from stats?",
			body: "This will exclude this enquiry from all conversion metrics and statistics. This action can be undone.",
			cancel: {
				label: "Cancel",
				action: () => {
					return null
				},
			},
			next: {
				label: "Yes, archive from stats",
				action: () => archiveFromStats(),
			},
		})
	}

	/**
	 * Archive the enquiry from stats
	 */
	const archiveFromStats = async () => {
		if (!enquiryID) return

		setArchiving(true)
		try {
			// Update the archived_from_stats field on the removed enquiry document
			await db.doc(`enquiries_removed/${enquiryID}`).set(
				{
					archived_from_stats: true,
				},
				{ merge: true }
			)
			setIsArchivedFromStats(true)
		} catch (err) {
			console.error("Error archiving enquiry from stats:", err)
		} finally {
			setArchiving(false)
		}
	}

	/**
	 * Restore the enquiry to stats
	 */
	const restoreToStats = async () => {
		if (!enquiryID) return

		setArchiving(true)
		try {
			// Update the archived_from_stats field on the removed enquiry document
			await db.doc(`enquiries_removed/${enquiryID}`).set(
				{
					archived_from_stats: false,
				},
				{ merge: true }
			)
			setIsArchivedFromStats(false)
		} catch (err) {
			console.error("Error restoring enquiry to stats:", err)
		} finally {
			setArchiving(false)
		}
	}

	if (!enquiryData) {
		return null
	}

	return (
		<Window close={close}>
			<WindowCard
				title="Viewing Archived Enquiry"
				actions={
					<Button
						small={true}
						label="Close"
						onClick={close}
					/>
				}>
				<div className="input-grid cols-4">
					<div className="span-2">
						<Input
							type="text"
							label="Site:"
							value={enquiryData.site || "Not specified"}
							readOnly={true}
							showLabel
						/>
					</div>
					<div className="span-2">
						<Input
							type="text"
							label="Status:"
							value={enquiryData.status || "Unknown"}
							readOnly={true}
							showLabel
						/>
					</div>

					<div className="span-2">
						<Input
							type="text"
							label="Client Name:"
							value={client.first_name || client.last_name ? `${client.first_name || ""} ${client.last_name || ""}`.trim() : "Not specified"}
							readOnly={true}
							showLabel
						/>
					</div>
					<div className="span-2">
						<Input
							type="text"
							label="Client Email:"
							value={client.email || "Not specified"}
							readOnly={true}
							showLabel
						/>
					</div>

					<div className="span-2">
						<Input
							type="text"
							label="Client Phone:"
							value={client.phone || "Not specified"}
							readOnly={true}
							showLabel
						/>
					</div>
					<div className="span-2">
						<Input
							type="text"
							label="Agent:"
							value={agent.first_name || agent.last_name ? `${agent.first_name || ""} ${agent.last_name || ""}`.trim() : "Not assigned"}
							readOnly={true}
							showLabel
						/>
					</div>

					<div className="span-2">
						<Input
							type="text"
							label="Location/Event:"
							value={enquiryData.location || "Not specified"}
							readOnly={true}
							showLabel
						/>
					</div>
					<div className="span-2">
						<Input
							type="text"
							label="Date From:"
							value={dateFromString}
							readOnly={true}
							showLabel
						/>
					</div>

					<Input
						type="text"
						label="Group Size:"
						value={enquiryData.group_size || "Not specified"}
						readOnly={true}
						showLabel
					/>
					<Input
						type="text"
						label="Nights:"
						value={enquiryData.nights || "Not specified"}
						readOnly={true}
						showLabel
					/>
					<Input
						type="text"
						label="Rounds:"
						value={enquiryData.rounds || "Not specified"}
						readOnly={true}
						showLabel
					/>
					<Input
						type="text"
						label="Quoted Amount:"
						value={`£${enquiryData.quoted || 0}`}
						readOnly={true}
						showLabel
					/>

					<div className="span-2">
						<Input
							type="text"
							label="Follow Up Date:"
							value={followUpString}
							readOnly={true}
							showLabel
						/>
					</div>
					<div className="span-2">
						<Input
							type="text"
							label="Created:"
							value={createdString}
							readOnly={true}
							showLabel
						/>
					</div>

					{enquiryData.notes && (
						<div className="span-4">
							<Textarea
								type="text"
								label="Private notes:"
								value={enquiryData.notes}
								readOnly={true}
								showLabel={true}
								rows={10}
							/>
						</div>
					)}
				</div>

				<div
					style={{
						marginTop: "20px",
						padding: "8px",
						backgroundColor: "#f8f9fa",
						border: "1px solid #dee2e6",
						borderRadius: "4px",
					}}>
					<p style={{ margin: 0, fontStyle: "italic", color: "#6c757d", fontSize: "12px" }}>This is an archived enquiry and cannot be edited.</p>
				</div>

				{isArchivedFromStats && (
					<div
						style={{
							marginTop: "12px",
							padding: "8px 12px",
							backgroundColor: "#FEF3C7",
							border: "1px solid #F59E0B",
							borderRadius: "4px",
						}}>
						<p style={{ margin: 0, fontWeight: 500, color: "#92400E", fontSize: "13px" }}>This enquiry is excluded from conversion statistics.</p>
					</div>
				)}

				<div className="ui-window-actions">
					{onReopen && (
						<Button
							small={true}
							label="Reopen Enquiry"
							onClick={() => {
								onReopen()
								close()
							}}
						/>
					)}

					{/* Archive from stats button - only visible to specific user */}
					{auth.currentUser?.uid === ARCHIVE_FROM_STATS_USER_ID && enquiryID && (
						<>
							{isArchivedFromStats ? (
								<Button
									small={true}
									className="OUTLINE"
									label="Restore to stats"
									loadingText="Restoring..."
									loading={archiving}
									onClick={() => restoreToStats()}
								/>
							) : (
								<Button
									small={true}
									className="OUTLINE"
									label="Archive from stats"
									loadingText="Archiving..."
									loading={archiving}
									onClick={() => checkToArchiveFromStats()}
								/>
							)}
						</>
					)}
				</div>
			</WindowCard>
		</Window>
	)
}

export default ViewArchivedEnquiry
